import joi from 'joi';
import {
  AllowedProvidersUrl,
  findOembedProviderUrl,
  isValidProvider,
} from '@loyalty/shared-common';
import { EmbeddedMediaComponentAttributes, SpotifySize } from '@loyalty/editor-components';
import { FormGroup } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InputTextField } from '../../../../shared/forms/fields/InputTextField';
import { SelectField } from '../../../../shared/forms/fields/SelectField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';

export const embeddedMediaWidgetTraitsSchema = joi.object<TraitFormValue>({
  // TODO this causes issues, I think the custom validation should return no error when value is undefined
  i18nAttributes: joi.object<EmbeddedMediaComponentAttributes>({
    targetUrl: joi
      .string()
      .optional()
      .empty('')
      .allow(null)
      .custom((val, helpers) => {
        if (!val) {
          return;
        }
        if (!isValidProvider(val)) {
          return helpers.error('custom.oembed.unsupportedProvider');
        }
      }),
    spotifySize: joi
      .string()
      .optional()
      .empty('')
      .allow(null)
      .valid(...Object.values(SpotifySize)),
  }),
});

type FormValue = TraitFormValue<void, EmbeddedMediaComponentAttributes>;

export function EmbeddedMediaWidgetTraits() {
  const { t } = useTranslation();
  const { watch, control } = useFormContext<FormValue>();

  const targetUrl = watch('i18nAttributes.targetUrl');

  const isSpotify = useMemo(() => {
    const providerUrl = findOembedProviderUrl(targetUrl);
    // typing is incorrect, we have to cast to any
    return providerUrl === AllowedProvidersUrl.SPOTIFY;
  }, [targetUrl]);

  return (
    <FormGroup>
      <Trait>
        <InputTextField<FormValue>
          name="i18nAttributes.targetUrl"
          label={t('editor.embedded-media.traits.target-url.label')}
          placeholder="https://youtu.be/bWdTRti57e4"
        />
      </Trait>
      {isSpotify && (
        <Trait>
          <SelectField
            control={control}
            name="i18nAttributes.spotifySize"
            label={t('editor.embedded-media.traits.spotify.size')}
            defaultValue={SpotifySize.MEDIUM}
            options={[
              {
                id: SpotifySize.SMALL,
                name: t('editor.embedded-media.traits.spotify.size.small'),
              },
              {
                id: SpotifySize.MEDIUM,
                name: t('editor.embedded-media.traits.spotify.size.medium'),
              },
              {
                id: SpotifySize.BIG,
                name: t('editor.embedded-media.traits.spotify.size.big'),
              },
            ]}
          />
        </Trait>
      )}
    </FormGroup>
  );
}
